/** @jsx jsx */

import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/core';

import { Colors, Icon as DSIcon, Text } from '@zapier/design-system';
import { createKeyHandler } from '@zapier/common-utils';

const Styles = {
  arrowIcon: css`
    transition: transform 0.2s ease-in-out;
    &.expanded {
      transform: rotate(180deg);
    }
  `,
  content: ({
    isExpanded,
    isExpandedFully,
  }: {
    isExpanded: boolean;
    isExpandedFully: boolean;
  }) => [
    css`
      flex: 1;
      display: grid;
      overflow: hidden;
      max-height: 400vh;
      transition: max-height 0.8s cubic-bezier(0.65, 0.05, 0.36, 1);
    `,
    !isExpanded &&
      css`
        margin-bottom: 0;
        max-height: 0;
        transition-duration: 0.4s;
      `,
    isExpanded &&
      isExpandedFully &&
      css`
        overflow: visible;
      `,
  ],
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 20px;
    padding-right: 8px;
    cursor: pointer;

    &:focus {
      outline: none;
    }
  `,
  root: (hasTopBorder: boolean) =>
    hasTopBorder &&
    css`
      :not(:first-of-type) {
        border-top: 1px solid ${Colors.GrayWarm3};
      }
    `,
  titleWrapper: css`
    display: flex;
    align-items: center;
    flex: 1;
  `,
};

type Props = {
  badge?: ReactNode;
  children: ReactNode;
  hasTopBorder?: boolean;
  isExpandedByDefault: boolean;
  isExpanded?: boolean;
  onClose?: () => void;
  onExpand?: () => void;
  title: string;
  titleIconName?: ComponentProps<typeof DSIcon>['name'];
};

const defaultProps = {
  hasTopBorder: false,
  isExpanded: false,
  isExpandedByDefault: true,
  onClose: () => undefined,
  onExpand: () => undefined,
};

function Accordion({
  badge,
  children,
  hasTopBorder = defaultProps.hasTopBorder,
  isExpandedByDefault = defaultProps.isExpandedByDefault,
  onClose = defaultProps.onClose,
  onExpand = defaultProps.onExpand,
  title,
  titleIconName,
}: Props) {
  const refContent = useRef(null);
  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);
  const [isExpandedFully, setIsExpandedFully] = useState(false);

  useEffect(() => {
    setIsExpanded(isExpandedByDefault);
  }, [isExpandedByDefault]);

  useEffect(() => {
    if (isExpanded) {
      onExpand();
    } else {
      onClose();
    }
  }, [isExpanded, onClose, onExpand]);

  useEffect(() => {
    const contentNode = refContent.current;
    contentNode.addEventListener('transitionend', () =>
      setIsExpandedFully(isExpanded)
    );
    return () => {
      contentNode.removeEventListener('transitionend', () =>
        setIsExpandedFully(isExpanded)
      );
    };
  }, [isExpanded]);

  return (
    <div css={Styles.root(hasTopBorder)}>
      <div
        css={Styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={createKeyHandler(['enter'], () =>
          setIsExpanded(!isExpanded)
        )}
        role="button"
        tabIndex={0}
      >
        <div css={Styles.titleWrapper}>
          {titleIconName && <DSIcon name={titleIconName} />}
          <Text fontWeight={600} margin="10px 0 10px 10px" type="Body4">
            {title}
          </Text>
          {badge}
        </div>
        <span className={isExpanded ? 'expanded' : ''} css={Styles.arrowIcon}>
          <DSIcon name="arrowSmallDown" />
        </span>
      </div>
      <div
        css={Styles.content({ isExpanded, isExpandedFully })}
        ref={refContent}
      >
        {children}
      </div>
    </div>
  );
}

export default Accordion;
